/**
 * CAOS-642 — how a place reads, and nothing else.
 *
 * This module imports nothing and defines no element. A caller that only needs the words — the host's
 * list-view data source, which is pure of the DOM — can have them without registering every component
 * in the library as a side effect of importing the `@caos/ui` barrel.
 */

/** A place as its two named subfields. */
export interface PlaceValue {
  readonly latitude: number;
  readonly longitude: number;
}

/**
 * A place as a person reads it: `37.7749° N, 122.4194° W`. Latitude first, each number named by its
 * hemisphere so nobody has to guess which is which, up to six decimals. Rounded BEFORE the hemisphere is
 * chosen, so a value that rounds to zero reads `0° N` and never `0° S`; 0, 0 is a real place and reads
 * `0° N, 0° E`.
 *
 * The kernel's display block says the same thing in the same words (`formatGeolocationDisplay` in
 * kernel/src/api/record-serialization.ts), which is what a list view shows, so the record page and a
 * list never show one place two ways.
 *
 * Both halves are pinned by the same cases, which is what holds them to that. `PLACE_READS` in
 * `caos-ui/test/location-field.test.ts` runs seven of them against `formatPlace`, and the CAOS-620
 * display block in `kernel/test/unit/api-geolocation-wire.test.ts` runs the same seven through
 * `serializeRecord(..., { display: true })`, which is how that rendering is reached from outside.
 * `PLACE_READS` names the kernel's file and undertakes to agree with it.
 *
 * They are two tables kept in step by hand rather than one shared fixture, and an earlier version
 * of this line said the two "share one table of cases in their tests", which overstated it in the
 * one way that matters: editing either table alone fails nothing. CAOS-1674 lifted the equivalent
 * cases for an address into `kernel/test/fixtures/address-display-cases.ts` instead, which gives
 * them a single named home but still crosses the package boundary by hand.
 */
export function formatPlace(place: PlaceValue, locale = 'en-US'): string {
  const number = new Intl.NumberFormat(locale, { maximumFractionDigits: 6, useGrouping: false });
  const axis = (n: number, positive: string, negative: string): string => {
    const rounded = Math.round(n * 1e6) / 1e6;
    return `${number.format(Math.abs(rounded))}° ${rounded < 0 ? negative : positive}`;
  };
  return `${axis(place.latitude, 'N', 'S')}, ${axis(place.longitude, 'E', 'W')}`;
}
